/**
 * Address Book Component
 */

import { useState, useEffect, useRef } from 'react';
import { accountApi } from '@/api/client';
import { useShop } from '@/context';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { AddressForm, type AddressFormRef } from '../Checkout/AddressForm';
import type { Address } from '@/types';
import styles from './AddressBook.module.css';

export function AddressBook() {
  const { t } = useShop();
  const [addresses, setAddresses] = useState<{
    billing: Address | null;
    shipping: Address | null;
  }>({ billing: null, shipping: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingType, setEditingType] = useState<'billing' | 'shipping' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formRef = useRef<AddressFormRef>(null);

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      const data = await accountApi.getAddresses();
      setAddresses(data);
    } catch (err) {
      setError('Erreur lors du chargement des adresses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingType || !formRef.current?.validate()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const formValues = formRef.current.getValues();

    setIsSaving(true);
    setError(null);

    try {
      const updated = await accountApi.updateAddresses({
        [editingType]: formValues,
      });

      setAddresses(updated);
      setEditingType(null);
      setSuccess('Adresse enregistrée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('myAddresses')}</h2>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      {editingType ? (
        <div className={styles.editForm}>
          <h3>
            {editingType === 'billing' ? 'Adresse de facturation' : 'Adresse de livraison'}
          </h3>
          <AddressForm
            ref={formRef}
            type={editingType}
            defaultValues={addresses[editingType] || {}}
            showEmail={editingType === 'billing'}
            showPhone
          />
          <div className={styles.formActions}>
            <Button
              variant="outline"
              onClick={() => setEditingType(null)}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t('save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.cards}>
          <AddressCard
            title="Adresse de facturation"
            address={addresses.billing}
            onEdit={() => setEditingType('billing')}
          />
          <AddressCard
            title="Adresse de livraison"
            address={addresses.shipping}
            onEdit={() => setEditingType('shipping')}
          />
        </div>
      )}
    </div>
  );
}

interface AddressCardProps {
  title: string;
  address: Address | null;
  onEdit: () => void;
}

function AddressCard({ title, address, onEdit }: AddressCardProps) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <button className={styles.editButton} onClick={onEdit}>
          Modifier
        </button>
      </div>
      {address ? (
        <div className={styles.cardContent}>
          <p>
            {address.first_name} {address.last_name}
          </p>
          {address.company && <p>{address.company}</p>}
          <p>{address.address_1}</p>
          {address.address_2 && <p>{address.address_2}</p>}
          <p>
            {address.postcode} {address.city}
          </p>
          <p>{address.country}</p>
          {address.phone && <p>Tél: {address.phone}</p>}
          {address.email && <p>{address.email}</p>}
        </div>
      ) : (
        <p className={styles.noAddress}>Aucune adresse enregistrée</p>
      )}
    </div>
  );
}
